import axios from 'axios';
import { baseAPI } from '../config';

/**
 * Las plantillas de WhatsApp de AR. Todo esto es de admin: las redacta ClaimUp.
 *
 * El backend responde `{ data }` / `{ error: { code, message } }`, así que aquí
 * se devuelve `data` pelado y se deja subir el error con **el mensaje del
 * backend**, no uno genérico: los rechazos que importan (una clave ocupada, un
 * texto que el cliente ya tiene, un envío sin número conectado) los explica él.
 */
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

async function pedir(promesa) {
  try {
    const { data } = await promesa;
    return data.data;
  } catch (err) {
    const msg = err.response?.data?.error?.message;
    throw msg ? Object.assign(new Error(msg), { code: err.response.data.error.code }) : err;
  }
}

/** El catálogo: el repertorio de ClaimUp, con quién tiene cada texto. */
export const getCatalogo = () =>
  pedir(axios.get(`${baseAPI}/kapso/templates`, { headers: headers() }));

export const editarPlantillaCatalogo = (key, cambios) =>
  pedir(axios.patch(`${baseAPI}/kapso/templates/${key}`, cambios, { headers: headers() }));

/** Las plantillas de un cliente, más lo que le queda por añadir del catálogo. */
export const getPlantillasDeCliente = companyId =>
  pedir(axios.get(`${baseAPI}/kapso/companies/${companyId}/templates`, { headers: headers() }));

/** Copia un texto del catálogo a la ficha del cliente. NO habla con Meta. */
export const anadirDelCatalogo = (companyId, key) =>
  pedir(
    axios.post(
      `${baseAPI}/kapso/companies/${companyId}/templates/from-catalogue`,
      { key },
      { headers: headers() }
    )
  );

/** Le pide a Meta que la revise. Solo si el cliente tiene número conectado. */
export const enviarAMeta = (companyId, key) =>
  pedir(
    axios.post(
      `${baseAPI}/kapso/companies/${companyId}/templates/${key}/submit`,
      {},
      { headers: headers() }
    )
  );

export const crearPlantillaDeCliente = (companyId, datos) =>
  pedir(
    axios.post(`${baseAPI}/kapso/companies/${companyId}/templates`, datos, { headers: headers() })
  );

/** Sube una plantilla propia al catálogo. Lleva el texto, NO la aprobación. */
export const promoverPlantilla = (companyId, key) =>
  pedir(
    axios.post(
      `${baseAPI}/kapso/companies/${companyId}/templates/${key}/promote`,
      {},
      { headers: headers() }
    )
  );

/**
 * Cambia el texto de la copia de un cliente. NO toca su estado de aprobación:
 * al cambiar el texto, lo aprobado deja de ser lo que se enviaría y la puerta
 * de envío se abre sola. Es lo que cierra el ciclo de un rechazo de Meta.
 */
export const editarPlantillaDeCliente = (companyId, key, cambios) =>
  pedir(
    axios.patch(
      `${baseAPI}/kapso/companies/${companyId}/templates/${key}`,
      cambios,
      { headers: headers() }
    )
  );

/** Lo que Meta dice de cada número conectado, de todas las empresas. */
export const getSaludDeNumeros = () =>
  pedir(axios.get(`${baseAPI}/kapso/phones/health`, { headers: headers() }));

/** Cuánto tardará en salir la cola de cada cliente, y si hará falta otro número. */
export const getProyeccionDeColas = () =>
  pedir(axios.get(`${baseAPI}/kapso/phones/projection`, { headers: headers() }));

// ── Propuestas del cliente (docs/ar-plantillas-propuestas-plan.md) ─────────
// El cliente propone, ClaimUp revisa y Meta aprueba.

/** Las plantillas de la propia empresa, y las variables que puede usar. */
export const getMisPlantillas = () => pedir(axios.get(`${baseAPI}/kapso/my/templates`, { headers: headers() }));

export const proponerPlantilla = datos =>
  pedir(axios.post(`${baseAPI}/kapso/my/templates`, datos, { headers: headers() }));

/** Corrige una propuesta, una devuelta o una rechazada por Meta: vuelve a revisión de ClaimUp. */
/**
 * Poner el peldaño de una plantilla. Cambia la escalera **al momento**: lo que
 * antes aprobaba ClaimUp son ahora dos topes del servidor —máximo 3 plantillas
 * en frío y 7 días entre ellas— (docs/escalera-con-topes-plan.md).
 *
 * `escalation_order: null` la saca de la escalera: se manda a mano y el agente
 * puede elegirla para un seguimiento.
 */
export const ponerPeldano = (key, escalationOrder) =>
  pedir(
    axios.patch(
      `${baseAPI}/kapso/my/templates/${key}/order`,
      { escalation_order: escalationOrder },
      { headers: headers() }
    )
  );

export const corregirPropuesta = (key, cambios) =>
  pedir(axios.patch(`${baseAPI}/kapso/my/templates/${key}`, cambios, { headers: headers() }));

/** La cola de ClaimUp: lo que han propuesto los clientes. */
export const getPropuestas = () =>
  pedir(axios.get(`${baseAPI}/kapso/templates/proposals`, { headers: headers() }));

/** ClaimUp la devuelve al cliente, con el motivo. */
export const devolverPropuesta = (companyId, key, reason) =>
  pedir(
    axios.post(
      `${baseAPI}/kapso/companies/${companyId}/templates/${key}/decline`,
      { reason },
      { headers: headers() }
    )
  );
